import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import apiUtils from '../../global/utils/api';

import {
  handleCreateFulfilled
  , handleFetchSinglePending
  , handleFetchSingleFulfilled
  , handleFetchSingleFromListFulfilled
  , handleFetchSingleRejected
  , handleFetchListPending
  , handleFetchListFulfilled
  , handleFetchListRejected
  , handleMutationPending
  , handleMutationFulfilled
  , handleMutationRejected
  , handleDeletePending
  , handleDeleteFulfilled
  , handleDeleteRejected
  , shouldFetch
  , INITIAL_STATE
  , handleInvalidateQuery
  , handleInvalidateQueries
  , handleAddSingleToList
} from '../../global/utils/storeUtils';


// CREATE
export const sendCreateComposerResource = createAsyncThunk(
  'composerResource/sendCreate'
  , async (newComposerResource) => {
    const endpoint = `/api/composer-resources`;
    const response = await apiUtils.callAPI(endpoint, 'POST', newComposerResource);
    return response;
  }
);

// READ
export const fetchSingleComposerResource = createAsyncThunk(
  'composerResource/fetchSingle'
  , async (id) => {
    const endpoint = `/api/composer-resources/${id}`;
    const response = await apiUtils.callAPI(endpoint);
    return response;
  }
);
export const fetchComposerResourceList = createAsyncThunk(
  'composerResource/fetchList'
  , async (listArgs) => {
    const endpoint = `/api/composer-resources${listArgs}`;
    const response = await apiUtils.callAPI(endpoint);
    return response;
  }
);

export const fetchSingleComposerResourceAtEndpoint = createAsyncThunk(
  'composerResource/fetchSingleWithFilter'
  , async (query) => {
    const endpoint = `/api/composer-resources${query}`
    const response = await apiUtils.callAPI(endpoint);
    return response;
  }
);
export const fetchComposerResourceListAtEndpoint = createAsyncThunk(
  'composerResource/fetchListWithFilter'
  , async (endpointWithQuery) => {
    // endpointWithQuery already carries its own leading slash from
    // useGetResourceList (e.g. `/logged-in?...`), so don't add another —
    // matches the sibling stores and avoids a `//` double-slash URL.
    const endpoint = `/api/composer-resources${endpointWithQuery}`;
    const response = await apiUtils.callAPI(endpoint);
    return response;
  }
);

// UPDATE
export const sendUpdateComposerResource = createAsyncThunk(
  'composerResource/sendUpdate'
  , async ({ _id, ...updates }) => {
    const endpoint = `/api/composer-resources/${_id}`;
    const response = await apiUtils.callAPI(endpoint, 'PUT', updates);
    return response;
  }
);

// DELETE
export const sendDeleteComposerResource = createAsyncThunk(
  'composerResource/sendDelete'
  , async (id) => {
    const endpoint = `/api/composer-resources/${id}`;
    const response = await apiUtils.callAPI(endpoint, 'DELETE');
    return response;
  }
);

const initialState = { ...INITIAL_STATE };

export const composerResourceSlice = createSlice({
  name: 'composerResource'
  , initialState
  , reducers: {
    invalidateQuery: handleInvalidateQuery
    , invalidateQueries: handleInvalidateQueries
    , addComposerResourceToList: handleAddSingleToList
  }
  , extraReducers: (builder) => {
    builder
      // CREATE
      .addCase(sendCreateComposerResource.fulfilled, handleCreateFulfilled)

      // READ
      .addCase(fetchSingleComposerResource.pending, handleFetchSinglePending)
      .addCase(fetchSingleComposerResource.fulfilled, handleFetchSingleFulfilled)
      .addCase(fetchSingleComposerResource.rejected, handleFetchSingleRejected)
      .addCase(fetchComposerResourceList.pending, handleFetchListPending)
      .addCase(fetchComposerResourceList.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'composerResources'))
      .addCase(fetchComposerResourceList.rejected, handleFetchListRejected)

      .addCase(fetchSingleComposerResourceAtEndpoint.pending, handleFetchSinglePending)
      .addCase(fetchSingleComposerResourceAtEndpoint.fulfilled, (state, action) => handleFetchSingleFromListFulfilled(state, action, 'composerResources'))
      .addCase(fetchSingleComposerResourceAtEndpoint.rejected, handleFetchSingleRejected)
      .addCase(fetchComposerResourceListAtEndpoint.pending, handleFetchListPending)
      .addCase(fetchComposerResourceListAtEndpoint.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'composerResources'))
      .addCase(fetchComposerResourceListAtEndpoint.rejected, handleFetchListRejected)

      // UPDATE
      .addCase(sendUpdateComposerResource.pending, handleMutationPending)
      .addCase(sendUpdateComposerResource.fulfilled, handleMutationFulfilled)
      .addCase(sendUpdateComposerResource.rejected, handleMutationRejected)

      // DELETE
      .addCase(sendDeleteComposerResource.pending, handleDeletePending)
      .addCase(sendDeleteComposerResource.fulfilled, handleDeleteFulfilled)
      .addCase(sendDeleteComposerResource.rejected, handleDeleteRejected)
  }
});

export const { invalidateQuery, invalidateQueries, addComposerResourceToList } = composerResourceSlice.actions;


export const fetchListIfNeeded = (queryKey, listFetch = fetchComposerResourceList) => (dispatch, getState) => {
  const composerResourceQuery = getState().composerResource.listQueries[queryKey];
  if(shouldFetch(composerResourceQuery)) {
    dispatch(listFetch(queryKey));
  }
};

export const fetchSingleIfNeeded = (id, singleFetch = fetchSingleComposerResource) => (dispatch, getState) => {
  const composerResourceQuery = getState().composerResource.singleQueries[id];
  if(shouldFetch(composerResourceQuery)) {
    dispatch(singleFetch(id));
  }
}

export default composerResourceSlice.reducer;
